import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { DropResult } from "react-beautiful-dnd";

import { faInfoCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/light";
import { faExclamationCircle } from "@awesome.me/kit-4c83d50d0d/icons/classic/regular";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import cx from "classnames";
import { omit } from "lodash";
import type { Moment } from "moment";

import Alert from "~/common/Alert";
import AppContext from "~/common/AppContext";
import type { AssigneeType, TeamType, UserType } from "~/common/AssigneeFilter";
import { confirm } from "~/lib/prompts";
import { commitMutationPromise } from "~/lib/relayUtil";
import { environment } from "~/models";
import EditableKeyResultList from "~/okrs/components/EditableKeyResultList";
import type { ObjectiveType as ParentObjectiveType } from "~/okrs/components/ObjectiveListInput";
import Settings from "~/okrs/components/Settings";
import { MetricProgresses, MetricTypes } from "~/okrs/components/TargetInput";
import EditObjectiveFromId from "~/okrs/EditObjectiveFromId";
import Objective, { type ObjectiveAttributes, objectiveCreateOrEditMutation, Visibility } from "~/okrs/Objective";
import { OKRsContext } from "~/okrs/ObjectiveList";
import { calculateKeyResultProgress, calculateObjectiveProgress } from "~/okrs/utils";
import { Button, useModals } from "~/ui";
import { HelpCenterIcon, HelpCenterLink, Modal } from "~/ui/legacyFellowIndex";
import toasty, { ToastActionButton } from "~/ui/toast";
import Tooltip from "~/ui/Tooltip";

import type {
	MetricProgress,
	MetricType,
	ObjectiveCreateOrEditMutation,
} from "~/__generated__/ObjectiveCreateOrEditMutation.graphql";
import type { ObjectiveStatus } from "~/__generated__/ObjectiveQueryQuery.graphql";
import type { teamQuery_Info$data as Team } from "~/__generated__/teamQuery_Info.graphql";

import styles from "./CreateObjective.module.css";

enum Alerts {
	MISSING_KEY_RESULTS = "You must set at least one key result",
	CREATE_ERROR = "An error occurred on objective creation. Please contact support",
	EMPTY_TITLE = "You must set the title",
}

const AlertIcons = {
	[Alerts.MISSING_KEY_RESULTS]: faExclamationCircle,
	[Alerts.CREATE_ERROR]: faExclamationCircle,
	[Alerts.EMPTY_TITLE]: faExclamationCircle,
};

const ErrorAlerts = [Alerts.MISSING_KEY_RESULTS, Alerts.CREATE_ERROR, Alerts.EMPTY_TITLE];

interface CreateObjectiveProps {
	onClose(objective?: ObjectiveAttributes): void;
	refetch?(objectiveId?: string): void;
	fromTeam?: Omit<Team, " $fragmentType">;
	preFilledObjective?: ObjectiveAttributes;
	hideSuccessToast?: boolean;
}

const initialObjectiveState = (
	user: UserType | undefined,
	team: Omit<Team, " $fragmentType"> | undefined,
): ObjectiveAttributes => {
	const initialKeyResult = {
		id: "",
		title: "",
		assigneeUser: !team
			? {
					id: user?.id || "",
					email: user?.email || "",
					image: user?.image || "",
					fullName: user?.fullName || "",
					isGuest: !!user?.isGuest,
				}
			: null,
		assigneeTeam: team
			? {
					id: team.id,
					title: team.title,
				}
			: null,
		metricProgress: MetricProgresses.INCREASING,
		metricType: MetricTypes.NUMBER,
		metricCurrency: "$",
		metricStart: 0,
		metricCurrent: 0,
		metricTarget: 100,
		dueDate: null,
		weight: 1,
		progress: 0,
		order: 0,
	};

	return {
		id: "",
		title: "",
		visibility: Visibility.PUBLIC,
		cycle: null,
		parent: null,
		status: "ON_TRACK",
		assigneeUser: !team ? user || null : null,
		assigneeTeam: team || null,
		dueDate: null,
		archived: false,
		weight: 0,
		contributors: [],
		contributorsTeams: [],
		keyResults: [initialKeyResult],
		description: null,
		progress: 0,
		hasAccess: true,
	};
};

export default function CreateObjective({
	onClose,
	refetch,
	fromTeam,
	preFilledObjective,
	hideSuccessToast,
}: CreateObjectiveProps) {
	const { user } = useContext(AppContext);
	const initialUser = {
		id: user?.id || "",
		email: user?.email || "",
		image: user?.image || "",
		fullName: user?.fullName || "",
		isGuest: !!user?.isGuest,
	};
	const [objective, _setObjective] = useState<ObjectiveAttributes>(
		preFilledObjective || initialObjectiveState(initialUser, fromTeam),
	);
	const [alert, setAlert] = useState<boolean>(false);
	const [alertMessage, setAlertMessage] = useState<Alerts | undefined>(undefined);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [refetchOnClose, setRefetchOnClose] = useState<boolean>(false);
	const [hadChanges, setHadChanges] = useState(false);
	const modals = useModals();

	const setObjective = useCallback((obj: ObjectiveAttributes) => {
		_setObjective(obj);
		setHadChanges(true);
	}, []);

	const onCloseWithConfirm = useCallback(async () => {
		if (
			hadChanges &&
			!(await confirm("Changes you've made so far will not be saved.", {
				yesText: "Discard",
				noText: "Keep editing",
				simple: true,
				title: "Discard unsaved changes?",
			}))
		) {
			return;
		}

		onClose();
	}, [hadChanges, onClose]);

	useEffect(() => {
		return () => {
			if (refetchOnClose) {
				refetch?.();
			}
		};
	}, [refetch, refetchOnClose]);

	const onTitleChange = useCallback(
		(newTitle: string) => {
			setAlert(false);
			setAlertMessage(undefined);
			setObjective({ ...objective, title: newTitle });
		},
		[objective, setObjective],
	);

	const onDescriptionChange = useCallback(
		(newDescription: string | null) => {
			setObjective({ ...objective, description: newDescription });
		},
		[objective, setObjective],
	);

	const onDueDateChange = useCallback(
		(newDueDate: Moment | null) => {
			setObjective({ ...objective, dueDate: newDueDate ? newDueDate.format("YYYY-MM-DD") : null });
		},
		[objective, setObjective],
	);

	const onStatusChange = useCallback(
		(newStatus: ObjectiveStatus) => {
			setObjective({ ...objective, status: newStatus });
		},
		[objective, setObjective],
	);

	const onAssigneeChange = useCallback(
		(newAssignee: UserType) => {
			setObjective({ ...objective, assigneeUser: newAssignee, assigneeTeam: null });
		},
		[objective, setObjective],
	);

	const onAssigneeTeamChange = useCallback(
		(newAssigneeTeam: TeamType) => {
			setObjective({ ...objective, assigneeUser: null, assigneeTeam: newAssigneeTeam });
		},
		[objective, setObjective],
	);

	const onCreateKeyResult = useCallback(() => {
		const newKeyResult = {
			id: "",
			title: "",
			assigneeUser: !fromTeam
				? {
						id: user?.id || "",
						email: user?.email || "",
						image: user?.image || "",
						fullName: user?.fullName || "",
						isGuest: !!user?.isGuest,
					}
				: null,
			assigneeTeam: fromTeam
				? {
						id: fromTeam.id,
						title: fromTeam.title,
					}
				: null,
			metricProgress: MetricProgresses.INCREASING,
			metricType: MetricTypes.NUMBER,
			metricCurrency: "$",
			metricStart: 0,
			metricCurrent: 0,
			metricTarget: 100,
			dueDate: objective.dueDate ?? null,
			weight: 1,
			progress: 0,
			order: objective.keyResults.length,
		};
		const newKeyResults = [...objective.keyResults, newKeyResult];
		setObjective({
			...objective,
			keyResults: newKeyResults,
			progress: calculateObjectiveProgress(newKeyResults, objective.children),
		});
		if (alert && alertMessage === Alerts.MISSING_KEY_RESULTS) {
			setAlert(false);
		}
	}, [user, objective, setObjective, alert, alertMessage, setAlert, fromTeam]);

	const onKeyResultTitleChange = useCallback(
		(newTitle: string, index: number) => {
			setObjective({
				...objective,
				keyResults: [
					...objective.keyResults.slice(0, index),
					{
						...objective.keyResults[index],
						title: newTitle,
					},
					...objective.keyResults.slice(index + 1),
				],
			});
		},
		[objective, setObjective],
	);

	const onKeyResultDueDateChange = useCallback(
		(newDueDate: Moment | null, index: number) => {
			setObjective({
				...objective,
				keyResults: [
					...objective.keyResults.slice(0, index),
					{
						...objective.keyResults[index],
						dueDate: newDueDate ? newDueDate.format("YYYY-MM-DD") : null,
					},
					...objective.keyResults.slice(index + 1),
				],
			});
		},
		[objective, setObjective],
	);

	const onKeyResultMetricChange = useCallback(
		(
			newMetricProgress: MetricProgress,
			newMetricType: MetricType,
			newMetricCurrency: string | undefined,
			newMetricStart: number,
			newMetricCurrent: number,
			newMetricTarget: number,
			index: number,
		) => {
			const newKeyResult = {
				...objective.keyResults[index],
				metricProgress: newMetricProgress,
				metricType: newMetricType,
				metricCurrency: newMetricCurrency ?? null,
				metricStart: newMetricStart,
				metricCurrent: newMetricCurrent,
				metricTarget: newMetricTarget,
			};
			const newKeyResults = [
				...objective.keyResults.slice(0, index),
				{
					...newKeyResult,
					progress: calculateKeyResultProgress(newKeyResult),
				},
				...objective.keyResults.slice(index + 1),
			];
			setObjective({
				...objective,
				keyResults: newKeyResults,
				progress: calculateObjectiveProgress(newKeyResults),
			});
		},
		[objective, setObjective],
	);

	const onKeyResultWeightChange = useCallback(
		(newWeight: number, index: number) => {
			const newKeyResult = {
				...objective.keyResults[index],
				weight: newWeight,
			};

			const newKeyResults = [
				...objective.keyResults.slice(0, index),
				{
					...newKeyResult,
					progress: calculateKeyResultProgress(newKeyResult),
				},
				...objective.keyResults.slice(index + 1),
			];

			setObjective({
				...objective,
				keyResults: newKeyResults,
				progress: calculateObjectiveProgress(newKeyResults),
			});
		},
		[objective, setObjective],
	);

	const onKeyResultOrderChange = useCallback(
		({ destination, source }: DropResult) => {
			if (!destination) {
				return;
			}

			const moveKeyResult = objective.keyResults[source.index];
			const keyResultsWithoutSource = [
				...objective.keyResults.slice(0, source.index),
				...objective.keyResults.slice(source.index + 1),
			];
			const keyResultsWithDestination = [
				...keyResultsWithoutSource.slice(0, destination.index),
				moveKeyResult,
				...keyResultsWithoutSource.slice(destination.index),
			];
			const newKeyResults = keyResultsWithDestination.map((kr, index) => ({ ...kr, order: index }));
			setObjective({ ...objective, keyResults: newKeyResults });
		},
		[objective, setObjective],
	);

	const onKeyResultAssigneeChange = useCallback(
		(user: UserType, index: number) => {
			setObjective({
				...objective,
				keyResults: [
					...objective.keyResults.slice(0, index),
					{
						...objective.keyResults[index],
						assigneeUser: user,
						assigneeTeam: null,
					},
					...objective.keyResults.slice(index + 1),
				],
			});
		},
		[objective, setObjective],
	);

	const onKeyResultAssigneeTeamChange = useCallback(
		(team: TeamType, index: number) => {
			setObjective({
				...objective,
				keyResults: [
					...objective.keyResults.slice(0, index),
					{
						...objective.keyResults[index],
						assigneeUser: null,
						assigneeTeam: team,
					},
					...objective.keyResults.slice(index + 1),
				],
			});
		},
		[objective, setObjective],
	);

	const onKeyResultDelete = useCallback(
		(index: number) => {
			const afterKeyResults = objective.keyResults.slice(index + 1).map(kr => ({ ...kr, order: kr.order - 1 }));
			const newKeyResults = [...objective.keyResults.slice(0, index), ...afterKeyResults];
			setObjective({
				...objective,
				keyResults: newKeyResults,
				progress: calculateObjectiveProgress(newKeyResults),
			});
		},
		[objective, setObjective],
	);

	const viewCreatedObjective = useCallback(
		(id: string) => () => {
			modals.open(({ close }) => <EditObjectiveFromId onClose={close} id={id} />);
		},
		[modals],
	);

	const onCreateObjective = useCallback(() => {
		if (objective.keyResults.length == 0) {
			setAlert(true);
			setAlertMessage(Alerts.MISSING_KEY_RESULTS);
			return;
		}

		if (objective.title.length === 0) {
			setAlert(true);
			setAlertMessage(Alerts.EMPTY_TITLE);
			return;
		}

		setIsLoading(true);
		commitMutationPromise<ObjectiveCreateOrEditMutation>(environment, {
			mutation: objectiveCreateOrEditMutation,
			variables: {
				objective: {
					title: objective.title,
					description: objective.description,
					assigneeUser: objective.assigneeUser?.id,
					assigneeTeam: objective.assigneeTeam?.id,
					status: objective.status,
					visibility: objective.visibility,
					dueDate: objective.dueDate,
					contributors: [...objective.contributors.map(c => c.id), ...objective.contributorsTeams.map(t => t.id)],
					cycle: objective.cycle?.id,
					parent: objective.parent?.id,
					keyResults: objective.keyResults.map(keyResult => ({
						...omit(keyResult, ["id", "progress"]),
						assigneeUser: keyResult.assigneeUser?.id,
						assigneeTeam: keyResult.assigneeTeam?.id,
						title: keyResult.title,
					})),
				},
			},
			onCompleted: response => {
				setIsLoading(false);

				if (!response.Objective_CreateEdit || !response.Objective_CreateEdit.objective) {
					setAlert(true);
					setAlertMessage(Alerts.CREATE_ERROR);
				} else {
					setRefetchOnClose(true);
					const { id, title } = response.Objective_CreateEdit.objective;
					if (!hideSuccessToast) {
						toasty.success(
							<>
								<strong>{title}</strong> created.
							</>,
							{
								autoClose: 5000,
								actions: <ToastActionButton onClick={viewCreatedObjective(id)}>Open</ToastActionButton>,
							},
						);
					}
					onClose(response.Objective_CreateEdit.objective);
				}
			},
		});
	}, [
		objective,
		setAlert,
		setAlertMessage,
		setIsLoading,
		setRefetchOnClose,
		onClose,
		viewCreatedObjective,
		hideSuccessToast,
	]);

	const onChangeVisibility = useCallback(
		(newVisibility: Visibility) => {
			setObjective({ ...objective, visibility: newVisibility });
		},
		[objective, setObjective],
	);

	const onChangeCycle = useCallback(
		(newCycle: string[]) => {
			// We have this call for a newly opened modal, because the default cycle is selected
			// I think it's not terrible, if we just disable the confirmation changes modal for the cycle
			// and use the hook directly
			_setObjective({ ...objective, cycle: { id: newCycle[0] || "" } });
		},
		[objective],
	);

	const onChangeParent = useCallback(
		(newParent: ParentObjectiveType | null) => {
			setObjective({ ...objective, parent: newParent });
		},
		[objective, setObjective],
	);

	const onChangeContributors = useCallback(
		(assignees: AssigneeType[]) => {
			const users = assignees.map(a => a.user).filter((u): u is UserType => !!u);
			const teams = assignees.map(a => a.team).filter((t): t is TeamType => !!t);
			setObjective({ ...objective, contributors: users, contributorsTeams: teams });
		},
		[objective, setObjective],
	);

	return (
		<Modal
			className={styles.modal}
			onClose={onCloseWithConfirm}
			data-test-id="create-objective-modal"
			// TODO: This is a placeholder solution the TargetInput Popup is having troubling focusing
			// Due to the focus lock on our modal (which uses reach-ui). We should remove this prop
			// And handle that issue ourselves and not remove the focus lock from the modal
			dangerouslyBypassFocusLock
		>
			<Modal.Header>
				<Modal.Title>Create new Objective</Modal.Title>
				<Modal.HeaderActions onClose={onCloseWithConfirm}>
					<HelpCenterIcon articleId="5607359" title="How to create new objectives" />
				</Modal.HeaderActions>
			</Modal.Header>

			{alert && alertMessage && (
				<Alert error={ErrorAlerts.includes(alertMessage)} icon={AlertIcons[alertMessage]}>
					{alertMessage}
				</Alert>
			)}

			<Modal.Body className={styles.body}>
				<OKRsContext.Provider
					value={useMemo<{ createView: boolean }>(() => {
						return { createView: true };
					}, [])}
				>
					<h3>
						Objective{" "}
						<Tooltip
							content={
								<>
									An objective should be inspirational, action-oriented, and set a clear direction. E.g. Drive revenue
									with Customer Success
								</>
							}
						>
							<FontAwesomeIcon icon={faInfoCircle} className={styles.popupIcon} />
						</Tooltip>
					</h3>
					<Objective
						{...objective}
						onAssigneeChange={onAssigneeChange}
						onAssigneeTeamChange={onAssigneeTeamChange}
						onTitleChange={onTitleChange}
						onDescriptionChange={onDescriptionChange}
						onDueDateChange={onDueDateChange}
						onStatusChange={onStatusChange}
						hasError={alert && alertMessage === Alerts.EMPTY_TITLE}
					/>
					<div className={styles.keyResults}>
						<h3>
							Key results{" "}
							<Tooltip
								content={
									<>A key result should be measurable, specific, and time-bound. E.g. Reduce churn from 10% to 3%</>
								}
							>
								<FontAwesomeIcon icon={faInfoCircle} className={styles.popupIcon} />
							</Tooltip>
						</h3>
						<EditableKeyResultList
							objective={objective}
							hasAccess
							onCreate={onCreateKeyResult}
							onTitleChange={onKeyResultTitleChange}
							onDueDateChange={onKeyResultDueDateChange}
							onAssigneeChange={onKeyResultAssigneeChange}
							onAssigneeTeamChange={onKeyResultAssigneeTeamChange}
							onMetricChange={onKeyResultMetricChange}
							onWeightChange={onKeyResultWeightChange}
							onOrderChange={onKeyResultOrderChange}
							onDelete={onKeyResultDelete}
							className={cx({ [styles.fieldError]: alert && alertMessage === Alerts.MISSING_KEY_RESULTS })}
						/>
					</div>
					<Settings
						objective={objective}
						hasAccess
						isCreate
						className={styles.settings}
						onChangeCycles={onChangeCycle}
						onChangeParent={onChangeParent}
						onChangeVisibility={onChangeVisibility}
						onChangeContributors={onChangeContributors}
					/>
				</OKRsContext.Provider>
			</Modal.Body>
			<Modal.Footer>
				<Modal.FooterSecondaryActions>
					<HelpCenterLink articleId="6319117">View OKR templates</HelpCenterLink>
				</Modal.FooterSecondaryActions>
				<Button variant="primary" size="normal" onClick={onCreateObjective} disabled={isLoading}>
					Create objective
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
